import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataSourceSettingsDto,
  ColoredThreshold,
  MutateWidgetPayload,
  Threshold,
  VariableDto,
  WidgetSettingsDto,
  WidgetSettingsType,
} from '@thingslog/repositories';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsFactory } from '../../../factories/WidgetSettings.factory';
import { QuerySettingsForm, createDefaultQuerySettings } from './query-settings/QuerySettingsForm';
import { WidgetVisualizationForm } from './WidgetVisualizationForm';

interface Props {
  type: 'create' | 'edit';
  dataSources: DataSourceSettingsDto[];
  variables: VariableDto[];
  defaultValues?: Partial<MutateWidgetPayload>;
  disabled?: boolean;
  onSubmit: (payload: MutateWidgetPayload) => void;
  onCancel: () => void;
}

const widgetTypeOptions: Array<{
  value: WidgetSettingsType;
  label: string;
  labelKey: string;
}> = [
  { value: 'line-chart', label: 'Line chart', labelKey: 'dashboard_widget_type_line_chart' },
  { value: 'bar-chart', label: 'Bar chart', labelKey: 'dashboard_widget_type_bar_chart' },
  { value: 'pie-chart', label: 'Pie chart', labelKey: 'dashboard_widget_type_pie_chart' },
  { value: 'area-chart', label: 'Area chart', labelKey: 'dashboard_widget_type_area_chart' },
  { value: 'gauge', label: 'Gauge', labelKey: 'dashboard_widget_type_gauge' },
  { value: 'value', label: 'Value', labelKey: 'dashboard_widget_type_value' },
  { value: 'table', label: 'Table', labelKey: 'dashboard_widget_type_table' },
];

const localizeDefaultThresholdLabel = (
  threshold: Threshold,
  index: number,
  t: TFunction
): string => {
  if (threshold.label === 'Critical') {
    return t('dashboard_visualization_threshold_critical', 'Critical');
  }

  if (threshold.label === 'Warning') {
    return t('dashboard_visualization_threshold_warning', 'Warning');
  }

  if (threshold.label) {
    return threshold.label;
  }

  return index === 1
    ? t('dashboard_visualization_threshold_critical', 'Critical')
    : t('dashboard_visualization_threshold_warning', 'Warning');
};

export const WidgetSettingsForm: FC<Props> = ({
  type,
  dataSources,
  variables,
  defaultValues,
  disabled = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const localizeDefaultThresholdLabels = useCallback(
    (settings: WidgetSettingsDto): WidgetSettingsDto => {
      if (settings.type !== 'gauge' && settings.type !== 'value') {
        return settings;
      }

      return {
        ...settings,
        visualization: {
          ...settings.visualization,
          thresholds: settings.visualization.thresholds.map(
            (threshold: ColoredThreshold, index: number): ColoredThreshold => ({
              ...threshold,
              label: localizeDefaultThresholdLabel(threshold, index, t),
            })
          ),
        },
      } as WidgetSettingsDto;
    },
    [t]
  );

  const defaultDataSource = useMemo(() => {
    return (
      dataSources.find(
        (dataSource: DataSourceSettingsDto): boolean =>
          dataSource.source.type === 'api-readings-device-sensor-list'
      ) ??
      dataSources[0] ??
      null
    );
  }, [dataSources]);

  const formDefaultValues = useMemo<MutateWidgetPayload>(() => {
    if (defaultValues?.settings) {
      return {
        settings: localizeDefaultThresholdLabels(
          WidgetSettingsFactory.normalize(defaultValues.settings)
        ),
      };
    }

    return {
      settings: localizeDefaultThresholdLabels(
        WidgetSettingsFactory.create({
          type: 'line-chart',
          title: '',
          dataSourceId: defaultDataSource?.id ?? '',
          query: createDefaultQuerySettings(
            defaultDataSource?.source.type ?? 'api-readings-device-sensor-list'
          ),
        })
      ),
    };
  }, [defaultValues, defaultDataSource, localizeDefaultThresholdLabels]);

  const { control, handleSubmit, setValue, watch } = useForm<MutateWidgetPayload>({
    mode: 'onChange',
    defaultValues: formDefaultValues,
  });

  const selectedDataSourceId = watch('settings.dataSourceId');
  const selectedWidgetType = watch('settings.type');

  const selectedDataSourceType = useMemo(() => {
    const match = dataSources.find(
      (dataSource: DataSourceSettingsDto) => dataSource.id === selectedDataSourceId
    );

    return match?.source.type;
  }, [dataSources, selectedDataSourceId]);

  const handleDataSourceChange = useCallback(
    (dataSourceId: string): void => {
      const match = dataSources.find(
        (dataSource: DataSourceSettingsDto): boolean => dataSource.id === dataSourceId
      );

      setValue('settings.dataSourceId', dataSourceId, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (!match) {
        return;
      }

      setValue('settings.query', createDefaultQuerySettings(match.source.type), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [dataSources, setValue]
  );

  const handleWidgetTypeChange = useCallback(
    (widgetType: WidgetSettingsType): void => {
      setValue('settings.type', widgetType, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue(
        'settings.visualization' as never,
        localizeDefaultThresholdLabels({
          type: widgetType,
          title: '',
          query: createDefaultQuerySettings(
            selectedDataSourceType ?? 'api-readings-device-sensor-list'
          ),
          dataSourceId: selectedDataSourceId,
          visualization: WidgetSettingsFactory.createDefaultVisualization(widgetType),
        } as WidgetSettingsDto).visualization as never,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [localizeDefaultThresholdLabels, selectedDataSourceId, selectedDataSourceType, setValue]
  );

  useEffect(() => {
    if (defaultValues?.settings || selectedDataSourceId || !defaultDataSource) {
      return;
    }

    handleDataSourceChange(defaultDataSource.id);
  }, [defaultValues, defaultDataSource, handleDataSourceChange, selectedDataSourceId]);

  const submitButtonText =
    type === 'create'
      ? t('dashboard_create_widget', 'Create Widget')
      : t('dashboard_save_widget', 'Save Widget');

  if (dataSources.length === 0) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 px-4 py-8 text-center">
        <Typography variant="subtitle1">
          {t('dashboard_no_data_sources_available', 'No data sources available')}
        </Typography>
        <Typography variant="body2" className="max-w-sm text-gray-600">
          {t(
            'dashboard_no_data_sources_description',
            'Configure a dashboard data source before creating or editing widgets.'
          )}
        </Typography>
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('close_btn', 'Close')}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-4 p-4"
      onSubmit={handleSubmit((payload: MutateWidgetPayload): void =>
        onSubmit({
          settings: WidgetSettingsFactory.normalize(payload.settings as WidgetSettingsDto),
        })
      )}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div data-dashboard-tour="widget-form-title">
          <Controller
            name="settings.title"
            control={control}
            rules={{
              required: t('dashboard_widget_title_required', 'Title is required'),
              maxLength: {
                value: 100,
                message: t(
                  'dashboard_widget_title_max_length',
                  'Title must be less than 100 characters'
                ),
              },
            }}
            render={({ field, fieldState }: FieldValues): JSX.Element => (
              <TextField
                {...field}
                label={t('dashboard_widget_title', 'Title')}
                size="small"
                fullWidth
                disabled={disabled}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </div>

        <div data-dashboard-tour="widget-form-type">
          <Controller
            name="settings.type"
            control={control}
            rules={{
              required: t('dashboard_widget_type_required', 'Widget type is required'),
            }}
            render={({ field, fieldState }: FieldValues): JSX.Element => (
              <FormControl fullWidth error={!!fieldState.error} size="small">
                <InputLabel>{t('dashboard_widget_type', 'Widget Type')}</InputLabel>
                <Select
                  {...field}
                  label={t('dashboard_widget_type', 'Widget Type')}
                  disabled={disabled}
                  onChange={(event: SelectChangeEvent<WidgetSettingsType>): void => {
                    field.onChange(event);
                    handleWidgetTypeChange(event.target.value as WidgetSettingsType);
                  }}
                >
                  {widgetTypeOptions.map(
                    (option: { value: WidgetSettingsType; label: string; labelKey: string }) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey, option.label)}
                      </MenuItem>
                    )
                  )}
                </Select>
                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </div>
      </div>

      <div data-dashboard-tour="widget-form-data-source">
        <Controller
          name="settings.dataSourceId"
          control={control}
          rules={{ required: t('dashboard_data_source_required', 'Data source is required') }}
          render={({ field, fieldState }: FieldValues): JSX.Element => (
            <FormControl fullWidth error={!!fieldState.error} size="small">
              <InputLabel>{t('dashboard_data_source', 'Data Source')}</InputLabel>
              <Select
                {...field}
                label={t('dashboard_data_source', 'Data Source')}
                disabled={disabled}
                onChange={(event: SelectChangeEvent<string>): void => {
                  field.onChange(event);
                  handleDataSourceChange(event.target.value as string);
                }}
              >
                {dataSources.map((dataSource: DataSourceSettingsDto) => (
                  <MenuItem key={dataSource.id} value={dataSource.id}>
                    {dataSource.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </div>

      <section
        className="flex min-w-0 flex-col gap-3 border-0 border-t border-solid border-gray-200 pt-4"
        data-dashboard-tour="widget-form-query-settings"
      >
        <Typography variant="subtitle2">
          {t('dashboard_query_settings', 'Query Settings')}
        </Typography>
        <QuerySettingsForm
          control={control}
          setValue={setValue}
          queryType={selectedDataSourceType}
          variables={variables}
          disabled={disabled}
        />
      </section>

      <WidgetVisualizationForm
        control={control}
        disabled={disabled}
        widgetType={selectedWidgetType}
      />

      <div
        className="flex justify-end gap-2 border-0 border-t border-solid border-gray-200 pt-4"
        data-dashboard-tour="widget-form-actions"
      >
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disableElevation disabled={disabled}>
          {submitButtonText}
        </Button>
      </div>
    </form>
  );
};
